import { useRef, useCallback, useState } from 'react';

interface VideoCaptureResult {
  stream: MediaStream | null;
  error: string | null;
  isCapturing: boolean;
  startCapture: (videoEl: HTMLVideoElement) => Promise<MediaStream | null>;
  stopCapture: () => void;
}

/**
 * Captures a MediaStream from an HTMLVideoElement playing a local file.
 * Uses captureStream() (Chrome/Firefox) with fallback detection.
 */
export function useVideoCapture(): VideoCaptureResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const startCapture = useCallback(async (videoEl: HTMLVideoElement): Promise<MediaStream | null> => {
    setError(null);

    // Check browser support
    const captureStreamFn =
      (videoEl as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream })
        .captureStream ??
      (videoEl as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream })
        .mozCaptureStream;

    if (!captureStreamFn) {
      const msg =
        'Your browser does not support video captureStream(). Please use Chrome or Firefox as the host.';
      setError(msg);
      return null;
    }

    try {
      // captureStream() works on modern browsers even when paused.
      // Never call unmuted videoEl.play() here as it triggers NotAllowedError before user interaction.
      let capturedStream = captureStreamFn.call(videoEl) as MediaStream;

      if (!capturedStream || capturedStream.getTracks().length === 0) {
        // Fallback: If browser requires playback to initialize tracks and video is already playing
        if (!videoEl.paused) {
          capturedStream = captureStreamFn.call(videoEl) as MediaStream;
        }
      }

      if (!capturedStream || capturedStream.getTracks().length === 0) {
        throw new Error('captureStream() returned an empty stream');
      }

      streamRef.current = capturedStream;
      setStream(capturedStream);
      setIsCapturing(true);
      return capturedStream;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to capture video stream';
      setError(msg);
      return null;
    }
  }, []);

  const stopCapture = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    setIsCapturing(false);
  }, []);

  return { stream, error, isCapturing, startCapture, stopCapture };
}
